import React, { useEffect, Suspense, lazy } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { usePostHog } from "posthog-js/react";
import { HubLegacyRedirect, DashboardLegacyRedirect, LibraryLegacyRedirect, MyRecordLegacyRedirect, SetupsLegacyRedirect, AskAiLegacyRedirect, SimulateLegacyRedirect, AutotradeLegacyRedirect } from "./components/routing/LegacyWorkflowRedirect";
import PilotGate from "./components/pilot/PilotGate";
import AdminGate from "./components/admin/AdminGate";
import BetaGate from "./components/beta/BetaGate";
import RebuildGate from "./components/terminal/RebuildGate";
import { PilotAccessProvider } from "./context/PilotAccessContext";
import { EarlyAccessProvider } from "./context/EarlyAccessContext";
import { ToastContainer } from "react-toastify";
import { Toaster } from 'react-hot-toast';
import "react-toastify/dist/ReactToastify.css";
import { GoogleOAuthProvider } from '@react-oauth/google';
import { TPSLProvider } from './context/TPSLContext';
import { OnboardingProvider } from './context/OnboardingContext';
import { ChartColorProvider } from './context/ChartColorContext';
import { SidebarProvider } from './context/SidebarContext';
import { AuthProvider } from './context/AuthContext';
import analytics from './services/analytics';

// Route-level code splitting: each page loads as its own chunk, so the entry
// bundle stops carrying every page's dependencies to every route (gsap on
// Landing, recharts on analytics, …).
const AlertsPage = lazy(() => import("./pages/AlertsPage"));
// Option A (broker-free, 24 Jul 2026): TradingDeployment and AccountSettingsPage
// DELETED — the product places no broker orders and offers no connect-your-broker
// flows (the /api/trading blueprint is gone). Do not reintroduce.
const OptionsRecoPage = lazy(() => import('./pages/OptionsRecoPage'));
const TrackerPage = lazy(() => import('./pages/TrackerPage'));
const MonitoringDashboard = lazy(() => import('./pages/MonitoringDashboard'));
const SetupAnalyticsPage = lazy(() => import('./pages/SetupAnalyticsPage'));
const LandingPage = lazy(() => import("./pages/LandingPage"));
const PrivateLoginPage = lazy(() => import("./pages/PrivateLoginPage"));
const SignInPage = lazy(() => import("./pages/SignInPage"));
const ManageUsersPage = lazy(() => import("./pages/ManageUsersPage"));
const DataQualityPage = lazy(() => import("./pages/DataQualityPage"));
const TriggerEvaluationPage = lazy(() => import("./pages/TriggerEvaluationPage"));
const PatternLabPage = lazy(() => import("./pages/PatternLabPage"));
const InvitePage = lazy(() => import("./pages/InvitePage"));
const GettingStartedPage = lazy(() => import('./pages/GettingStartedPage'));
// KAN-72/73 rebuild namespace — new IA mounts here behind RebuildGate while
// the old routes stay untouched; they flip to redirects at P6 rollout.
const TerminalShellPage = lazy(() => import('./pages/terminal/TerminalShellPage'));
const TerminalHomePage = lazy(() => import('./pages/terminal/TerminalHomePage'));
const TerminalStockPage = lazy(() => import('./pages/terminal/TerminalStockPage'));
const TerminalListPage = lazy(() => import('./pages/terminal/TerminalListPage'));
const TerminalSetupsPage = lazy(() => import('./pages/terminal/TerminalSetupsPage'));
const TerminalSectorPage = lazy(() => import('./pages/terminal/TerminalSectorPage'));
const TerminalSetupPage = lazy(() => import('./pages/terminal/TerminalSetupPage'));
const TerminalComboPage = lazy(() => import('./pages/terminal/TerminalComboPage'));
const TerminalPlaybookPage = lazy(() => import('./pages/terminal/TerminalPlaybookPage'));
const TerminalPlaybookSetupPage = lazy(() => import('./pages/terminal/TerminalPlaybookSetupPage'));
const TerminalPlaybookBuildPage = lazy(() => import('./pages/terminal/TerminalPlaybookBuildPage'));
const TerminalPlaybookStrategyPage = lazy(() => import('./pages/terminal/TerminalPlaybookStrategyPage'));
const TerminalRecordPage = lazy(() => import('./pages/terminal/TerminalRecordPage'));
// pages/static/index.js is a barrel of NAMED re-exports; React.lazy needs a
// module whose default export is the component, so import each file directly.
const TermsAndConditions = lazy(() => import("./pages/static/TermsAndConditions"));
const PrivacyPolicy = lazy(() => import("./pages/static/PrivacyPolicy"));
const ContactUs = lazy(() => import("./pages/static/ContactUs"));
const HowToUse = lazy(() => import("./pages/static/HowToUse"));
const CheckDemo = lazy(() => import("./pages/static/CheckDemo"));
const Glossary = lazy(() => import("./pages/static/Glossary"));

// Shown while a route chunk downloads (usually one short flash per page).
function RouteFallback() {
  return (
    <div className="min-h-screen flex flex-col items-center justify-center gap-4 bg-slate-50 dark:bg-slate-900">
      <img src="/symbol.svg" alt="True Setups" className="w-14 h-14 rounded-xl animate-pulse" />
      <div
        className="h-6 w-6 rounded-full border-2 border-slate-300 dark:border-slate-600 border-t-violet-500 animate-spin"
        role="status"
        aria-label="Loading page"
      />
    </div>
  );
}

// React Router doesn't restore scroll position on navigation the way a
// full page load does — without this, a route pushed while scrolled down
// (e.g. opening a setup from partway down the Playbook categories list)
// renders the new page already scrolled to wherever the old page's scroll
// position happened to land, sometimes straight to its bottom.
function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

// Component to handle route-based analytics
function AnalyticsTracker() {
  const location = useLocation();
  const posthog = usePostHog();

  useEffect(() => {
    const pageName = getPageName(location.pathname);
    analytics.trackPageView(pageName);
    // Explicit SPA pageview for PostHog — autocapture misses window.location.href navigations
    posthog?.capture('$pageview', {
      $current_url: window.location.href,
      page_name: pageName,
      path: location.pathname,
    });
  }, [location, posthog]);

  return null;
}

// Helper function to get readable page names with business context
function getPageName(pathname) {
  const pageMap = {
    '/': 'True Setups - AI-Powered Stock Analysis & Trading Signals',
    '/login': 'Sign In - True Setups',
    '/sign-in': 'Sign In - True Setups',
    '/welcome': 'Getting Started - True Setups',
    '/library': 'Playbook - Setup Reference',
    '/terminal/playbook': 'Playbook - Setup Reference',
    '/ask-ai': 'Ask AI - Search & Discovery',
    '/setups': 'Terminal - Market Context & Performance',
    '/terminal': 'Terminal - Market Context & Performance',
    '/terminal/record': 'Record - Paper trading journal',
    '/hub': 'Ask AI - Search & Discovery',
    '/dashboard': 'Scan - Historical Setup Analysis',
    '/alerts': 'Alerts - Saved Filter Notifications',
    // Option A (broker-free, 24 Jul 2026): /trading/deploy and /account/settings
    // routes DELETED — no broker orders, no broker connections. Do not reintroduce.
    '/autotrade': 'Paper trade - Paper trading sessions',
    '/simulate': 'Simulate - Guided paper trading',
    '/my-record': 'My Record - Paper trading journal',
    '/options/reco': 'Options - Lab',
    '/options/tracker': 'Options Tracker - Lab',
    '/contact': 'Contact True Setups Support',
    '/monitoring': 'System Monitoring - Job Execution Dashboard',
    '/analytics': 'Setup Analytics - Signal Quality Dashboard',
    '/admin/users': 'Manage Users - Admin',
    '/admin/data-quality': 'Data Quality - Admin',
    '/admin/trigger-evaluation': 'Trigger Evaluation - Admin',
    '/admin/pattern-lab': 'Pattern Lab',
    '/how-to-use': 'How to Use True Setups - Platform Tutorial',
    '/check-demo': 'Check Demo - What is a setup? - True Setups',
    '/glossary': 'Playbook - True Setups',
    '/terms': 'Terms of Service - True Setups',
    '/privacy': 'Privacy Policy - Data Protection',
    '/invite': 'Pilot Invite - True Setups'
  };
  
  return pageMap[pathname] || `True Setups - ${pathname.replace('/', '').replace('-', ' ')}`;
}

function App() {
  useEffect(() => {
    // Initialize analytics on app start
    analytics.init();
  }, []);

  return (
    <GoogleOAuthProvider clientId="80321912630-jrva5ft6eots802vficckkkshmt22q4b.apps.googleusercontent.com">
      <AuthProvider>
      <ChartColorProvider>
        <TPSLProvider>
          <OnboardingProvider>
          <ToastContainer position="top-right" autoClose={3000} />
          <Toaster 
            position="top-center"
            containerStyle={{ top: '5rem', zIndex: 10001 }}
            containerClassName="toast-app"
            gutter={12}
            toastOptions={{
              className: 'toast-card',
              duration: 4000,
              style: {
                padding: '14px 20px',
                borderRadius: '12px',
                fontSize: '14px',
                fontWeight: 500,
              },
              success: {
                duration: 4000,
                style: {
                  borderLeft: '4px solid #8b5cf6',
                },
                iconTheme: {
                  primary: '#8b5cf6',
                  secondary: '#fff',
                },
              },
              error: {
                duration: 4000,
                iconTheme: {
                  primary: '#ef4444',
                  secondary: '#fff',
                },
              },
            }}
          />
          <ScrollToTop />
          <AnalyticsTracker />
          <EarlyAccessProvider>
          <PilotAccessProvider>
          <Suspense fallback={<RouteFallback />}>
          <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/login" element={<PrivateLoginPage />} />
          <Route path="/sign-in" element={<SignInPage />} />
          <Route path="/invite/:token" element={<InvitePage />} />
          <Route path="/welcome" element={<PilotGate><GettingStartedPage /></PilotGate>} />
          <Route path="/hub" element={<HubLegacyRedirect />} />
          <Route path="/library" element={<LibraryLegacyRedirect />} />
          <Route path="/ask-ai" element={<AskAiLegacyRedirect />} />
          {/* /terminal is the app's canonical main entry (WORKFLOW_ROUTES.setups
              and every internal link now point there); /setups is kept as a
              working alias to the same TerminalHomePage for old links/bookmarks. */}
          <Route path="/setups" element={<RebuildGate><TerminalHomePage /></RebuildGate>} />
          <Route path="/setups/legacy" element={<SetupsLegacyRedirect />} />
          <Route path="/dashboard" element={<DashboardLegacyRedirect />} />
          <Route path="/alerts" element={<PilotGate><AlertsPage /></PilotGate>} />
          {/* Option A (broker-free, 24 Jul 2026): /account/settings (broker
              connections) and /trading/deploy (portfolio execution) routes
              DELETED — no broker orders, no broker connections. Do not reintroduce. */}
          <Route path="/autotrade" element={<AutotradeLegacyRedirect />} />
          <Route path="/simulate" element={<SimulateLegacyRedirect />} />
          <Route path="/my-record" element={<MyRecordLegacyRedirect />} />
          <Route path="/terminal" element={<RebuildGate><TerminalHomePage /></RebuildGate>} />
          <Route path="/terminal/foundation" element={<RebuildGate><TerminalShellPage /></RebuildGate>} />
          <Route path="/terminal/list" element={<RebuildGate><TerminalListPage /></RebuildGate>} />
          <Route path="/terminal/setups" element={<RebuildGate><TerminalSetupsPage /></RebuildGate>} />
          <Route path="/terminal/sector/:name" element={<RebuildGate><TerminalSectorPage /></RebuildGate>} />
          <Route path="/terminal/playbook" element={<RebuildGate><TerminalPlaybookPage /></RebuildGate>} />
          <Route path="/terminal/playbook/build" element={<RebuildGate><TerminalPlaybookBuildPage /></RebuildGate>} />
          <Route path="/terminal/playbook/strategy/:id" element={<RebuildGate><TerminalPlaybookStrategyPage /></RebuildGate>} />
          <Route path="/terminal/playbook/setup/:name" element={<RebuildGate><TerminalPlaybookSetupPage /></RebuildGate>} />
          <Route path="/terminal/record" element={<RebuildGate><TerminalRecordPage /></RebuildGate>} />
          <Route path="/terminal/stock/:symbol" element={<RebuildGate><TerminalStockPage /></RebuildGate>} />
          {/* Indices get their own path so a URL reads as what it opens
              (/terminal/index/^NSEI, not /terminal/stock/^NSEI). Same page —
              it already branches on the symbol. The /stock/ path still serves
              indices so existing links and bookmarks keep working. */}
          <Route path="/terminal/index/:symbol" element={<RebuildGate><TerminalStockPage /></RebuildGate>} />
          <Route path="/terminal/setup/:anchor" element={<RebuildGate><TerminalSetupPage /></RebuildGate>} />
          <Route path="/terminal/combo/:id" element={<RebuildGate><TerminalComboPage /></RebuildGate>} />
          {/* KAN-63 item 1: options surfaces are lab-only — compiled out of the
              public build entirely (see StockAnalysis docs/LAB.md). */}
          {process.env.REACT_APP_LAB === '1' && (
            <>
              <Route path="/options/reco" element={<PilotGate><OptionsRecoPage /></PilotGate>} />
              <Route path="/options/tracker" element={<PilotGate><TrackerPage /></PilotGate>} />
            </>
          )}
          <Route path="/monitoring" element={<PilotGate><MonitoringDashboard /></PilotGate>} />
          <Route path="/analytics" element={<PilotGate><SetupAnalyticsPage /></PilotGate>} />
          <Route path="/admin/users" element={<PilotGate><AdminGate><ManageUsersPage /></AdminGate></PilotGate>} />
          <Route path="/admin/data-quality" element={<PilotGate><AdminGate><DataQualityPage /></AdminGate></PilotGate>} />
          <Route path="/admin/trigger-evaluation" element={<PilotGate><AdminGate><TriggerEvaluationPage /></AdminGate></PilotGate>} />
          <Route path="/admin/pattern-lab" element={<PilotGate><BetaGate><PatternLabPage /></BetaGate></PilotGate>} />
          {/* Alerts route removed - will be reimplemented with filter-based architecture */}
          <Route path="/team" element={<Navigate to="/" replace />} />
          <Route path="/contact" element={<ContactUs />} />
          <Route path="/how-to-use" element={<HowToUse />} />
          <Route path="/check-demo" element={<CheckDemo />} />
          <Route path="/glossary" element={<Glossary />} />
          <Route path="/terms" element={<TermsAndConditions />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route
            path="*"
            element={<h2 className="text-center">404&nbsp;-&nbsp;Page&nbsp;Not&nbsp;Found</h2>}
          />
          </Routes>
          </Suspense>
          </PilotAccessProvider>
          </EarlyAccessProvider>
          </OnboardingProvider>
        </TPSLProvider>
      </ChartColorProvider>
      </AuthProvider>
    </GoogleOAuthProvider>
  );
}

export default App;
